import { SegmentedControl } from '@mantine/core'
import { findCar, formatLapTime, sessionBestLapMs } from '@racedash/shared'
import { Datum, NoValue, Tile } from '../Tile'
import type { WidgetEditorProps, WidgetProps } from './props'

/**
 * The last lap, or the best of the session so far.
 *
 * Best lap is derived by the interpreter rather than here: a tile holding ten
 * laps cannot see the other six hundred, and the alternative was a second, much
 * larger query for a single number.
 */
export default function LapTime({ config, car, data }: WidgetProps<'lapTime'>) {
  const standing = findCar(data.standings, car)
  const best = config.which === 'best'
  const ms = best ? standing?.bestLapMs : standing?.lastLapMs

  if (ms == null || ms <= 0) {
    return (
      <Tile label={best ? 'Best lap' : 'Last lap'}>
        <NoValue as="time" />
      </Tile>
    )
  }

  // Worth saying when a car's own best is also the fastest anyone has done: it
  // is the one lap time on the screen that means something to everybody.
  const fastest = sessionBestLapMs(data.standings?.cars ?? [])
  const isSessionBest = best && fastest != null && ms <= fastest

  return (
    <Tile label={best ? 'Best lap' : 'Last lap'}>
      <Datum value={formatLapTime(ms)} as="time" sub={isSessionBest ? 'session best' : undefined} />
    </Tile>
  )
}

export function Editor({ config, onChange }: WidgetEditorProps<'lapTime'>) {
  return (
    <SegmentedControl
      fullWidth
      value={config.which}
      onChange={(v) => onChange({ kind: 'lapTime', which: v as 'last' | 'best' })}
      data={[
        { value: 'last', label: 'Last lap' },
        { value: 'best', label: 'Best lap' },
      ]}
    />
  )
}
